// One settings group, open: its pages down the left (Connected services lists
// its overview first, and a group of one page still lists that page, so every
// page sits in the same place) beside the open page. Every page
// is drawn by Page.jsx: its header, its tabs, then its body from the registry
// (SettingsSections.jsx). Pages and tabs come from lib/settingsModel.js.
import { phAttrs } from "../../lib/analytics/attrs.js";
import { ATTRIBUTE } from "../../lib/analytics/surfaceVocabulary.js";
import { sourceByKey } from "../../pages/dataSources/model.js";
import { SourceLogo, toneDotClass } from "../statusCard.jsx";
import ServicesOverview from "./ConnectedServices.jsx";
import Page, { PAGE_PART } from "./Page.jsx";
import SectionBody from "./SettingsSections.jsx";

const SERVICES = "services";

// The menu's width, which the strip over the group (SettingsTop.jsx) lines up with.
export const MENU_COLUMNS = "grid-cols-[224px_minmax(0,1fr)]";

function NavItem({ label, address, on, onOpen, end = null, page }) {
    return (
        <button
            type="button"
            data-settings-page={page}
            data-settings-section={address}
            aria-current={on ? "page" : undefined}
            onClick={() => onOpen(address)}
            {...phAttrs({ [ATTRIBUTE.SETTING]: address })}
            className={`relative flex min-h-[32px] w-full cursor-pointer items-center gap-[10px] rounded-[6px] border-0 px-[10px] py-[6px] text-left text-[13.5px] leading-[1.3] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)] ${on
                ? "bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] font-medium text-[color:var(--ink)]"
                : "bg-transparent text-[color:var(--muted)] hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] hover:text-[color:var(--ink)]"}`}
        >
            {/* The open page's green mark, the accent the sidebar's open row has. */}
            {on ? <span aria-hidden="true" className="absolute bottom-[7px] left-0 top-[7px] w-[2px] rounded-full bg-[color:var(--green)]" /> : null}
            <span className="min-w-0 flex-1">{label}</span>
            {end}
        </button>
    );
}

// A tab or a page without tabs: one section's body, its sections drawn as one
// (`merged`), or each section under its own name alone (the page's
// description already says what they hold).
function PartBody({ part, bodyProps }) {
    if (part.merged || part.sections.length === 1) {
        return <SectionBody address={part.sections[0].address} sections={new Set(part.sections.map((s) => s.id))} {...bodyProps} />;
    }
    return (
        <div className="grid min-w-0 gap-[28px]">
            {part.sections.map((section, i) => (
                <section key={section.id} aria-label={section.name} data-settings-part={section.address} className={`grid min-w-0 gap-[16px] ${i ? "border-t border-[color:var(--line)] pt-[26px]" : ""}`}>
                    <h3 className={PAGE_PART}>{section.name}</h3>
                    <SectionBody address={section.address} {...bodyProps} />
                </section>
            ))}
        </div>
    );
}

// `page` and `tab` are what the address opened (null for the group alone);
// `summary` is the group's read (settingsSummaries.js): `values` by section
// beside its page's name, `statuses` for the services, `now` for their overview.
export default function SettingsGroup({ group, page = null, tab = null, onOpen, bodyProps, summary = {} }) {
    const services = group.id === SERVICES;
    const current = page ?? (services ? null : group.pages[0]);
    const part = current ? tab ?? current.tabs?.[0] ?? current : null;

    const open = current ? (
        <Page
            key={current.id}
            title={current.name}
            description={current.peek}
            lead={services ? <SourceLogo logo={sourceByKey(current.id)?.logo} size={26} /> : null}
            tabs={current.tabs?.map((t) => ({ id: t.id, label: t.name, address: t.address }))}
            tab={part.id}
            onTab={(id) => onOpen(current.tabs.find((t) => t.id === id).address)}
            tabAttrs={(t) => ({ "data-settings-section": t.address, ...phAttrs({ [ATTRIBUTE.SETTING]: t.address }) })}
            data-settings-current={current.address}
        >
            <PartBody key={part.id} part={part} bodyProps={bodyProps} />
        </Page>
    ) : (
        <Page title={group.name} description={summary.now ?? group.peek}>
            <ServicesOverview group={group} onOpen={onOpen} statuses={summary.statuses ?? null} />
        </Page>
    );

    const end = (p) => {
        if (services) {
            const status = summary.statuses?.[p.id];
            return status ? <span className="flex shrink-0 items-center" title={status.label}><span aria-hidden="true" className={`h-[7px] w-[7px] rounded-full ${toneDotClass(status.tone)}`} /><span className="sr-only">{status.label}</span></span> : null;
        }
        const value = summary.values?.[p.sections[0].id];
        return value ? <span className="shrink-0 text-[12px] font-normal tabular-nums text-[color:var(--muted)]">{value}</span> : null;
    };

    return (
        <section aria-label={group.name} data-settings-open={group.id} className={`grid w-full min-w-0 flex-1 ${MENU_COLUMNS} border-t border-[color:var(--line)] max-[799px]:grid-cols-1`}>
            <nav aria-label={`${group.name} pages`} className="sticky top-0 grid content-start gap-[2px] self-start pb-[22px] pr-[14px] pt-[20px] max-[799px]:static max-[799px]:border-b max-[799px]:border-[color:var(--line)]">
                <h2 className="m-0 px-[10px] pb-[10px] text-[13px] font-semibold leading-[1.3] text-[color:var(--ink)]">{group.name}</h2>
                {services ? <NavItem label="Overview" address={group.id} page="overview" on={!current} onOpen={onOpen} /> : null}
                {group.pages.map((p) => (
                    <NavItem key={p.id} page={p.id} label={p.name} address={p.address} on={p.id === current?.id} onOpen={onOpen} end={end(p)} />
                ))}
            </nav>
            {/* 32px from the menu's rule on the left; on the right, 22px here and
                the page's own 10px inset make the same 32. */}
            <div className="flex min-w-0 flex-col border-l border-[color:var(--line)] pb-[28px] pl-[32px] pr-[22px] pt-[22px] max-[799px]:border-l-0 max-[799px]:px-0">{open}</div>
        </section>
    );
}
